import React, { useState } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import { useAuth } from '../context/AuthContext';
    import ParentForm from './ParentForm';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiEdit, FiTrash2, FiMail, FiPhone, FiUser, FiShield, FiCheck, FiX, FiSearch, FiFilter, FiArrowRight } = FiIcons;

    const ParentManagement = () => {
      const { parents, deleteParent, students, users } = useData();
      const { deleteUser } = useAuth();
      const [showAddForm, setShowAddForm] = useState(false);
      const [editingParent, setEditingParent] = useState(null);
      const [searchTerm, setSearchTerm] = useState('');
      const [loading, setLoading] = useState({});

      const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this parent? This will also remove their user account if it exists.')) {
          setLoading(prev => ({ ...prev, [id]: true }));
          try {
            const userAccount = users.find(user => user.id === id || user.email === parents.find(p => p.id === id)?.email);
            const result = deleteParent(id);
            if (!result.success) {
              alert(result.message);
              return;
            }
            if (userAccount) {
              const userResult = await deleteUser(userAccount.id);
              if (!userResult.success) {
                console.warn('Parent deleted but user account deletion failed:', userResult.message);
              }
            }
          } catch (error) {
            console.error('Error deleting parent:', error);
            alert('Error deleting parent. Please try again.');
          } finally {
            setLoading(prev => ({ ...prev, [id]: false }));
          }
        }
      };

      const getStudentCount = (parentId) => {
        return students.filter(student => student.parentId === parentId).length;
      };

      const getUserAccountInfo = (parent) => {
        return users.find(user => user.id === parent.id || (user.email === parent.email && user.role === 'parent'));
      };

      const filteredParents = parents.filter(p => {
        const search = searchTerm.toLowerCase();
        return (
          p.firstName.toLowerCase().includes(search) ||
          p.lastName.toLowerCase().includes(search) ||
          p.email.toLowerCase().includes(search) ||
          p.phone.includes(search)
        );
      });

      return (
        <div className="space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h2 className="text-4xl font-black text-gray-900 tracking-tight">Parents</h2>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest">Portal Access & Family Profiles</p>
            </div>
            <motion.button 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }} 
              onClick={() => setShowAddForm(true)} 
              className="flex items-center gap-3 px-8 py-4 bg-indigo-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-indigo-100 transition-all hover:bg-indigo-700"
            >
              <SafeIcon icon={FiPlus} className="text-lg" />
              Add Parent
            </motion.button>
          </div>

          <div className="bg-white p-4 rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input 
                type="text" 
                placeholder="Search parents by name, email or phone..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-[1.8rem] focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-gray-700" 
              />
            </div>
            <div className="bg-indigo-50 px-6 py-4 rounded-[1.8rem] flex items-center gap-3">
              <SafeIcon icon={FiShield} className="text-indigo-600" />
              <span className="text-[10px] font-black text-indigo-900 uppercase tracking-widest">
                {parents.length} Registered Families
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode='popLayout'>
              {filteredParents.map((parent, index) => {
                const userAccount = getUserAccountInfo(parent);
                const studentCount = getStudentCount(parent.id);
                const isLoading = loading[parent.id];
                
                return (
                  <motion.div 
                    layout 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, scale: 0.9 }} 
                    key={parent.id} 
                    className="bg-white rounded-[2.5rem] p-8 shadow-sm border-2 border-transparent hover:border-indigo-600/10 hover:shadow-2xl hover:shadow-indigo-100 transition-all group relative overflow-hidden"
                  >
                    <div className="flex justify-between items-start mb-8">
                      <div className="w-16 h-16 bg-indigo-50 rounded-[1.5rem] flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                        <SafeIcon icon={FiUser} className="text-3xl" />
                      </div>
                      {userAccount ? (
                        <span className="text-[10px] font-black px-4 py-1.5 bg-emerald-50 text-emerald-600 rounded-full tracking-widest uppercase flex items-center gap-1">
                          <SafeIcon icon={FiCheck} /> Portal Active
                        </span>
                      ) : (
                        <span className="text-[10px] font-black px-4 py-1.5 bg-gray-100 text-gray-400 rounded-full tracking-widest uppercase">
                          No Account
                        </span>
                      )}
                    </div>

                    <h3 className="text-2xl font-black text-gray-900 mb-1 tracking-tight">{parent.firstName} {parent.lastName}</h3>
                    <p className="text-xs font-bold text-gray-400 mb-6 uppercase tracking-widest truncate">{parent.email}</p>

                    <div className="space-y-3 mb-8">
                      <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <SafeIcon icon={FiPhone} className="text-indigo-400" />
                        {parent.phone}
                      </div>
                      <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                        <SafeIcon icon={FiArrowRight} className="text-indigo-400" />
                        {studentCount} Linked Students
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => setEditingParent(parent)} 
                        className="flex-1 bg-gray-900 hover:bg-black text-white py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg"
                      >
                        Edit Profile
                      </button>
                      <button 
                        onClick={() => handleDelete(parent.id)} 
                        disabled={isLoading}
                        className="p-3.5 bg-rose-50 text-rose-300 rounded-2xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                      >
                        {isLoading ? <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> : <SafeIcon icon={FiTrash2} />}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {showAddForm && <ParentForm onClose={() => setShowAddForm(false)} onSave={() => setShowAddForm(false)} />}
          {editingParent && <ParentForm parent={editingParent} onClose={() => setEditingParent(null)} onSave={() => setEditingParent(null)} />}
        </div>
      );
    };

    export default ParentManagement;